import { ArtifactActionsFooter } from "@/components/artifacts/ArtifactActionsFooter.tsx";
import {
  ArtifactShareControl,
  canShareArtifact,
} from "@/components/artifacts/ArtifactShareControl.tsx";
import { ArtifactErrorBoundary } from "@/components/artifacts/ArtifactErrorBoundary.tsx";
import { ArtifactThreadLink } from "@/components/artifacts/ArtifactThreadLink.tsx";
import { ClaimedArtifactView } from "@/components/artifacts/ClaimedArtifactView.tsx";
import { isArtifactReadOnly } from "@/components/artifacts/artifactUtils.ts";
import { ArtifactView } from "@/components/artifacts/view/ArtifactView.tsx";
import {
  MainContentWrapper,
  SiteHeaderContent,
  ViewWrapper,
} from "@/components/layout/ViewWrapper.tsx";
import { Loader } from "@/components/loader/Loader.tsx";
import { EntityAgentComposer } from "@/components/messages/Omnibox/EntityAgentComposer.tsx";
import { getThreadPagePath } from "@/components/threads/util.ts";
import { Card } from "@/components/ui/card.tsx";
import { artifactQueries } from "@/hooks/artifactQueries.ts";
import { useArtifactLiveUpdates } from "@/components/artifacts/useArtifactLiveUpdates.ts";
import { useBackNavigation } from "@/hooks/useBackNavigation.ts";
import type { HeavyArtifactResponse } from "@/types/artifacts.ts";
import type { ClaimedArtifactResp } from "@/types/sharedArtifact.ts";
import { artifactExpandedTitle } from "@/shared/artifactTitle.ts";
import { toUrnString } from "@/types/urn.ts";
import { useGateValue } from "@statsig/react-bindings";
import { useQuery } from "@tanstack/react-query";
import { useEffect, type ReactNode } from "react";
import { useLocation, useParams, useSearch } from "wouter";
import { getErrorStatus } from "@/shared/retryPolicy.ts";

/** Full-page fallback for artifact links when their containing surface has no canvas. */
export const Artifact = () => {
  // Matched by both `/artifacts/:artifactId` (bare) and the type-qualified
  // `/artifacts/:artifactType/:artifactId`; `useParams` reads whichever hit.
  const { artifactType, artifactId } = useParams<{ artifactType?: string; artifactId: string }>();
  const search = useSearch();
  const isSharedThread = new URLSearchParams(search).has("shared");
  const [, setLocation] = useLocation();
  const navigateBack = useBackNavigation();
  const userIsAdmin = useGateValue("internal-features");
  const { data, error, isError, isLoading } = useQuery(artifactQueries.byId(artifactId));

  const artifact = data?.isHeavy ? data : null;
  // A claimed read — someone else's artifact, reached through a share grant —
  // carries no thread or message, so everything thread-scoped has to key off
  // this rather than off a missing `threadId` (which would also match a genuine
  // failure on the owner path).
  const isClaimed = artifact?.isOwner === false;
  const heavy = artifact && !isClaimed ? (artifact as HeavyArtifactResponse) : null;
  // `isOwner` is optional on both arms of the response union, so it discriminates
  // at runtime but not for the compiler — same reason `heavy` above is asserted.
  const claimed = artifact && isClaimed ? (artifact as ClaimedArtifactResp) : null;

  // Same one-shot-fetch staleness the canvas modal has: without this the footer
  // keeps offering an action the artifact has already moved past. A claimed
  // artifact has no thread to listen to, and no actions to go stale.
  useArtifactLiveUpdates(artifactId, heavy?.threadId);

  // The page is id-authoritative (fetch is by id), so a type-qualified URL whose
  // type disagrees with the loaded artifact would advertise a false URN. Once
  // loaded, self-heal the URL to the canonical type; the bare-id route (no type
  // segment) is left as-is. Preserve the query string so `?shared` survives.
  useEffect(() => {
    if (artifact && artifactType !== undefined && artifactType !== artifact.artifactType) {
      setLocation(
        `/artifacts/${artifact.artifactType}/${artifact.id}${search ? `?${search}` : ""}`,
        { replace: true },
      );
    }
  }, [artifact, artifactType, search, setLocation]);

  const threadId = heavy?.threadId ?? undefined;
  let content: ReactNode;

  if (isLoading) {
    content = <Loader className="py-8" />;
  } else if (isError) {
    // 404 is the server's answer for "you have no access to this", not a
    // failure — it is what a cross-account read returns and what a revoked
    // share falls back to, so the copy has to work for both without confirming
    // the artifact exists.
    content =
      getErrorStatus(error) === 404 ? (
        <div className="p-4 text-muted-foreground text-sm">
          <p>This item isn&apos;t available.</p>
          <p className="mt-2">
            It may have been removed, or shared with a different account than the one you&apos;re
            signed in to.
          </p>
        </div>
      ) : (
        <p className="p-4 text-muted-foreground text-sm">This artifact couldn't be loaded.</p>
      );
  } else if (!artifact?.view) {
    content = (
      <p className="p-4 text-muted-foreground text-sm">This artifact couldn't be loaded.</p>
    );
  } else if (claimed) {
    content = (
      <ClaimedArtifactView
        artifact={claimed}
        view={artifact.view}
        userIsAdmin={userIsAdmin}
        onRequestClose={navigateBack}
      />
    );
  } else if (!heavy?.view || threadId === undefined) {
    content = (
      <p className="p-4 text-muted-foreground text-sm">This artifact couldn't be loaded.</p>
    );
  } else {
    const isReadOnly = isArtifactReadOnly(heavy, isSharedThread);
    content = (
      <>
        <ArtifactErrorBoundary
          key={heavy.id}
          artifact={heavy}
          threadId={threadId}
          messageId={heavy.messageId}
          userIsAdmin={userIsAdmin}
        >
          <ArtifactView
            view={heavy.view}
            threadId={threadId}
            messageId={heavy.messageId}
            artifactId={heavy.id}
            isReadOnly={isReadOnly}
            isSharedThread={isSharedThread}
            maximized
            onRequestClose={navigateBack}
          />
        </ArtifactErrorBoundary>
        {!isSharedThread && (
          <>
            <div className="flex flex-col-reverse gap-2 border-t px-5 py-3 md:flex-row md:items-center md:justify-between">
              <ArtifactActionsFooter artifact={heavy} threadId={threadId} onFired={navigateBack} />
              <div className="flex items-center gap-2">
                {/* This page shows the artifact with no conversation around it,
                    so it needs the way back to the thread that made it. A shared
                    thread's viewer can't reach that thread, hence the guard above. */}
                <ArtifactThreadLink onOpen={() => setLocation(getThreadPagePath(threadId))} />
                {canShareArtifact(heavy, { isSharedThread }) && (
                  <ArtifactShareControl
                    artifactId={heavy.id}
                    artifactType={heavy.artifactType}
                    title={artifactExpandedTitle(heavy)}
                  />
                )}
              </div>
            </div>
            {/* Its own row below the declarative actions: those commit the
                artifact (Send / Cancel), this opens a conversation about it. */}
            <div className="border-t px-5 py-3">
              <EntityAgentComposer
                urn={toUrnString({ type: heavy.artifactType, id: heavy.id })}
                label="Ask with AI"
                placeholder="Ask about this or request a change…"
                statsigName="artifact-ask-with-ai"
              />
            </div>
          </>
        )}
      </>
    );
  }

  return (
    <ViewWrapper
      headerContent={
        <SiteHeaderContent
          includeBackButton
          title={artifact ? artifactExpandedTitle(artifact) : "Artifact"}
        />
      }
    >
      <MainContentWrapper columns={7} className="py-4">
        <Card className="overflow-hidden">{content}</Card>
      </MainContentWrapper>
    </ViewWrapper>
  );
};
